/**
 * Admin area: retreats, briefs, scheduling, duplication and cloning.
 *
 * Deliberately plain — this is a working surface for one person, not a
 * product screen. It reuses the same tokens, on the wider admin column.
 */

import { apiGet, apiPost, apiPut, apiDelete, ApiError } from '../js/api.js'
import { parseUtc } from '../js/dates.js'
import { downscaleAll } from '../js/image.js'
import { icon } from '../js/icons.js'
import { text } from '../js/html.js'
import { adminHeader, mountAdminHeader } from '../components/adminShell.js'

/** An orb message is a line, not a page (REDESIGN.md §9). */
const ORB_MAX = 90

const KINDS = [
  ['brief', 'Brief'],
  ['episode', 'Episode'],
  ['mission', 'Mission'],
  ['info', 'Practical'],
]

const STATUSES = [
  ['draft', 'Draft'],
  ['scheduled', 'Scheduled'],
  ['sent', 'Sent'],
]

// ---------------------------------------------------------------------------
// Retreat list
// ---------------------------------------------------------------------------

export async function renderAdminRetreats(root, { onNavigate, onExit }) {
  root.innerHTML = `<div class="admin"><p class="lede">Loading…</p></div>`

  const { retreats, timezones: zones } = await apiGet('/admin/retreats')
  let cloning = null
  let creating = false
  let error = null

  const onOpen = (id) => onNavigate(`/admin/retreat/${id}`)

  function draw() {
    root.innerHTML = `
      <div class="admin">
        ${adminHeader({ tab: 'retreats', subtitle: countLine(retreats) })}

        ${error ? `<p class="notice notice--error">${text(error)}</p>` : ''}

        <div class="acards">
          ${retreats.map(retreatCard).join('')}
          <button class="acard acard--new" data-new>
            <span class="acard__plus" aria-hidden="true">+</span>
            <span class="acard__name">New retreat</span>
            <span class="acard__meta">Empty, with nothing in it yet</span>
          </button>
        </div>

        ${cloning === null ? '' : cloneForm(retreats.find((r) => r.id === cloning))}
        ${creating ? newRetreatForm() : ''}
      </div>
    `

    mountAdminHeader(root, { onNavigate, onExit })
    root.querySelectorAll('[data-open]').forEach((button) =>
      button.addEventListener('click', () => onOpen(Number(button.dataset.open))),
    )
    root.querySelector('[data-new]')?.addEventListener('click', () => {
      creating = true
      cloning = null
      error = null
      draw()
    })
    root.querySelector('#new-form')?.addEventListener('submit', submitNew)
    root.querySelector('#new-cancel')?.addEventListener('click', () => {
      creating = false
      draw()
    })
    root.querySelectorAll('[data-clone]').forEach((button) =>
      button.addEventListener('click', () => {
        cloning = Number(button.dataset.clone)
        error = null
        draw()
      }),
    )
    root.querySelector('#clone-form')?.addEventListener('submit', submitClone)
    root.querySelector('#clone-cancel')?.addEventListener('click', () => {
      cloning = null
      draw()
    })
  }

  /**
   * A retreat as a card rather than a table row.
   *
   * There are never many of them — one live, one template, a handful archived —
   * and the thing you want is to go into one. A row of six cells with a link in
   * the first one made that the smallest target on the screen.
   */
  function retreatCard(retreat) {
    return `
      <article class="acard acard--${text(retreat.status)}">
        <button class="acard__open" data-open="${retreat.id}">
          <span class="acard__name">${text(retreat.name)}</span>
          <span class="acard__meta">${text(retreat.starts_on)} → ${text(retreat.ends_on)}</span>
        </button>
        <p class="acard__facts">
          <span class="pill pill--${text(retreat.status)}">${statusLabel(retreat.status)}</span>
          <span>${retreat.brief_count} briefs</span>
          <span>${retreat.member_count} people</span>
        </p>
        <p class="acard__actions">
          <button class="link-button" data-open="${retreat.id}">Open</button>
          <button class="link-button" data-clone="${retreat.id}">Clone</button>
        </p>
      </article>
    `
  }

  function countLine(list) {
    const live = list.filter((r) => r.status === 'active').length

    return `${list.length} ${list.length === 1 ? 'retreat' : 'retreats'}, ${live} live`
  }

  /**
   * A retreat with nothing in it. Cloning the scenario is the usual way in, so
   * this is deliberately the quieter of the two: it exists for the case the
   * template does not fit, not as the default route.
   */
  function newRetreatForm() {
    const today = new Date().toISOString().slice(0, 10)

    return `
      <form class="card admin-form" id="new-form">
        <h2>New retreat</h2>
        <p class="lede">
          Empty. Nothing is copied into it — if you want the scenario, clone it
          instead.
        </p>

        <label class="field">
          <span class="field__label">Name</span>
          <input class="field__input" name="name" required placeholder="Maldives, March 2027" />
        </label>

        <div class="admin-form__grid">
          <label class="field">
            <span class="field__label">Where</span>
            <input class="field__input" name="location" maxlength="120" placeholder="Fehendhoo, Maldives" />
          </label>

          <label class="field">
            <span class="field__label">Call it a…</span>
            <input class="field__input" name="place_noun" maxlength="40" value="retreat" />
          </label>

          <label class="field">
            <span class="field__label">First day</span>
            <input class="field__input" type="date" name="starts_on" required value="${today}" />
          </label>

          <label class="field">
            <span class="field__label">Last day</span>
            <input class="field__input" type="date" name="ends_on" required value="${today}" />
          </label>

          <label class="field">
            <span class="field__label">Timezone</span>
            <select class="field__input" name="timezone">
              ${zones.map((zone) => `<option value="${text(zone)}">${text(zone)}</option>`).join('')}
            </select>
          </label>
        </div>

        <div class="admin-form__actions">
          <button class="button button--primary" type="submit">Create</button>
          <button class="button button--ghost" type="button" id="new-cancel">Cancel</button>
        </div>
      </form>
    `
  }

  async function submitNew(event) {
    event.preventDefault()

    const payload = Object.fromEntries(new FormData(event.target))

    try {
      const result = await apiPost('/admin/retreats', payload)
      onNavigate(`/admin/retreat/${result.retreat.id}/settings`)
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'Could not create the retreat.'
      draw()
    }
  }

  function cloneForm(retreat) {
    return `
      <form class="card admin-form" id="clone-form">
        <h2>Clone “${text(retreat.name)}”</h2>
        <p class="lede">
          Every date is recomputed to keep the same distance from day one and the
          same local time. Personal briefs are not copied.
        </p>

        <label class="field">
          <span class="field__label">Name of the new retreat</span>
          <input class="field__input" name="name" required value="${text(retreat.name)} — copy" />
        </label>

        <label class="field">
          <span class="field__label">First day (D1)</span>
          <input class="field__input" type="date" name="starts_on" required />
        </label>

        <label class="field">
          <span class="field__label">Retreat timezone</span>
          <select class="field__input" name="timezone">
            <option value="">${text(retreat.timezone)} (unchanged)</option>
            ${zones.map((zone) => `<option value="${text(zone)}">${text(zone)}</option>`).join('')}
          </select>
        </label>

        <div class="admin-form__actions">
          <button class="button button--primary" type="submit">Clone</button>
          <button class="button button--ghost" type="button" id="clone-cancel">Cancel</button>
        </div>
      </form>
    `
  }

  async function submitClone(event) {
    event.preventDefault()

    const data = Object.fromEntries(new FormData(event.target))

    try {
      const result = await apiPost(`/admin/retreats/${cloning}/clone`, data)
      cloning = null
      onOpen(result.retreat_id)
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'Could not clone the retreat.'
      draw()
    }
  }

  draw()
}

// ---------------------------------------------------------------------------
// Briefs of one retreat
// ---------------------------------------------------------------------------

export async function renderAdminBriefs(root, { retreatId, onNavigate, onExit }) {
  root.innerHTML = `<div class="admin"><p class="lede">Loading…</p></div>`

  let data = await apiGet(`/admin/retreats/${retreatId}/briefs`)
  let editing = null // brief object, or 'new'
  let orbForm = false
  let actsOpen = false
  let actEditing = null // the key of the chapter being written
  let error = null
  let notice = null

  async function reload() {
    data = await apiGet(`/admin/retreats/${retreatId}/briefs`)
  }

  function draw() {
    const now = parseUtc(data.server_time)

    root.innerHTML = `
      <div class="admin">
        ${adminHeader({
          retreat: data.retreat,
          tab: 'story',
          subtitle: `${data.briefs.length} ${data.briefs.length === 1 ? 'point' : 'points'}`,
        })}

        <div class="atoolbar">
          <button class="button button--primary admin__new" id="new">New brief</button>
          <button class="link-button" id="acts">${actsOpen ? 'Hide chapters' : 'Chapters'}</button>
          <button class="link-button" id="orb">${orbForm ? 'Hide orb message' : 'Send orb message'}</button>
        </div>

        ${error ? `<p class="notice notice--error">${text(error)}</p>` : ''}
        ${notice ? `<p class="notice notice--info">${text(notice)}</p>` : ''}

        ${actsOpen ? actsPanel() : ''}
        ${orbForm === false ? '' : orbQuickAction()}
        ${editing === null ? '' : briefForm(editing)}

        ${
          data.briefs.length === 0
            ? '<p class="lede">Nothing here yet. Start with a brief, or clone the scenario.</p>'
            : chapters(data.briefs)
                .map((group, index) => chapterBlock(group, now, index === 0))
                .join('')
        }
      </div>
    `

    mountAdminHeader(root, { onNavigate, onExit })
    root.querySelector('#acts').addEventListener('click', () => {
      actsOpen = !actsOpen
      error = null
      draw()
    })
    root.querySelector('#acts-form')?.addEventListener('submit', saveAct)
    root.querySelectorAll('[data-act-edit]').forEach((button) =>
      button.addEventListener('click', () => {
        // The Clear button carries an empty key, which means "back to adding".
        actEditing = button.dataset.actEdit === '' ? null : button.dataset.actEdit
        error = null
        draw()
      }),
    )
    root.querySelectorAll('[data-act-delete]').forEach((button) =>
      button.addEventListener('click', () => {
        if (!window.confirm('Remove this chapter\u2019s copy? The briefs in it are untouched.')) {
          return
        }

        act(() => apiDelete(`/admin/retreats/${retreatId}/acts/${encodeURIComponent(button.dataset.actDelete)}`))
      }),
    )
    root.querySelector('#orb').addEventListener('click', () => {
      orbForm = !orbForm
      error = null
      draw()
    })
    root.querySelector('#orb-form')?.addEventListener('submit', sendOrb)
    root.querySelector('#orb-cancel')?.addEventListener('click', () => {
      orbForm = false
      draw()
    })
    root.querySelector('#orb-message')?.addEventListener('input', (event) => {
      const left = ORB_MAX - event.target.value.length
      root.querySelector('#orb-left').textContent = `${left}`
      root.querySelector('#orb-left').classList.toggle('is-over', left < 0)
    })
    root.querySelector('#new').addEventListener('click', () => {
      editing = 'new'
      error = null
      draw()
    })

    root.querySelectorAll('[data-edit]').forEach((button) =>
      button.addEventListener('click', () => {
        editing = data.briefs.find((brief) => brief.id === Number(button.dataset.edit))
        error = null
        draw()
      }),
    )
    root.querySelectorAll('[data-send]').forEach((button) =>
      button.addEventListener('click', () => {
        if (!window.confirm('Publish and send now? Push goes to everyone who has not had it yet.')) {
          return
        }

        act(async () => {
          const result = await apiPost(`/admin/briefs/${button.dataset.send}/send`)
          notice = `Sent: ${result.pushes} push, ${result.emails} email, ${result.skipped} skipped.`
        })
      }),
    )
    root.querySelectorAll('[data-duplicate]').forEach((button) =>
      button.addEventListener('click', () => act(() => apiPost(`/admin/briefs/${button.dataset.duplicate}/duplicate`))),
    )
    root.querySelectorAll('[data-delete]').forEach((button) =>
      button.addEventListener('click', () => {
        if (window.confirm('Delete this brief? It cannot be undone.')) {
          act(() => apiDelete(`/admin/briefs/${button.dataset.delete}`))
        }
      }),
    )

    root.querySelector('#brief-image')?.addEventListener('change', async (event) => {
      const file = event.target.files?.[0]

      if (file === undefined) return

      const form = new FormData()
      form.append('image', file)

      await act(() => apiPost(`/admin/briefs/${editing.id}/image`, form))
    })

    root.querySelector('#brief-image-clear')?.addEventListener('click', () =>
      act(() => apiPost(`/admin/briefs/${editing.id}/image`, new FormData())),
    )

    root.querySelector('#brief-form')?.addEventListener('submit', submitBrief)
    root.querySelector('#brief-cancel')?.addEventListener('click', () => {
      editing = null
      draw()
    })
  }

  /**
   * The chapters of this retreat.
   *
   * A brief says which act it belongs to; this is where the act gets its name
   * and the line Path opens with. Every act any brief mentions is listed, even
   * one nobody has written copy for yet — otherwise an act could be set on a
   * brief and then be invisible in the only place it can be named.
   */
  function actsPanel() {
    const written = new Map((data.acts ?? []).map((row) => [row.key, row]))
    const keys = [...new Set([...(data.act_keys ?? []), ...written.keys()])]
    const current = actEditing === null ? null : (written.get(actEditing) ?? { key: actEditing })

    return `
      <section class="card admin-form">
        <h2>Chapters</h2>
        <p class="hint">
          Path leads with the chapter the newest open brief belongs to. An act with
          no copy here falls back to the app's own wording.
        </p>

        ${
          keys.length === 0
            ? '<p class="lede">No brief names an act yet. Set one on a brief first.</p>'
            : `<table class="admin-table">
                 <thead>
                   <tr><th>Act</th><th>Title</th><th>Lede</th><th></th></tr>
                 </thead>
                 <tbody>
                   ${keys.map((key) => actRow(key, written.get(key))).join('')}
                 </tbody>
               </table>`
        }

        <form class="admin-form" id="acts-form">
          <p class="admin-form__section">${current === null ? 'Add a chapter' : `Write: ${text(current.key)}`}</p>

          <div class="admin-form__grid">
            <label class="field">
              <span class="field__label">Act</span>
              <input class="field__input" name="key" maxlength="40" required
                     value="${text(current?.key)}" placeholder="ACT II" />
              <span class="hint">Exactly as it is written on the briefs.</span>
            </label>

            <label class="field">
              <span class="field__label">Order</span>
              <input class="field__input" type="number" name="sort_order"
                     value="${current?.sort_order ?? keys.length}" />
            </label>
          </div>

          <label class="field">
            <span class="field__label">Title</span>
            <input class="field__input" name="title" maxlength="120" required
                   value="${text(current?.title)}" placeholder="Rhythm" />
          </label>

          <label class="field">
            <span class="field__label">Lede</span>
            <input class="field__input" name="lede" maxlength="400"
                   value="${text(current?.lede)}"
                   placeholder="The same skeleton every day. What changes is you." />
          </label>

          <div class="admin-form__actions">
            <button class="button button--primary" type="submit">Save chapter</button>
            ${
              actEditing === null
                ? ''
                : '<button class="button button--ghost" type="button" data-act-edit="">Clear</button>'
            }
          </div>
        </form>
      </section>
    `
  }

  function actRow(key, row) {
    return `
      <tr>
        <td><code>${text(key)}</code></td>
        <td>${row ? text(row.title) : '<span class="admin-table__muted">not written</span>'}</td>
        <td class="admin-table__muted">${text(row?.lede)}</td>
        <td class="admin-table__actions">
          <button class="link-button" data-act-edit="${text(key)}">${row ? 'Edit' : 'Write'}</button>
          ${row ? `<button class="link-button link-button--danger" data-act-delete="${text(key)}">Remove</button>` : ''}
        </td>
      </tr>
    `
  }

  async function saveAct(event) {
    event.preventDefault()

    const payload = Object.fromEntries(new FormData(event.target))

    await act(async () => {
      await apiPost(`/admin/retreats/${retreatId}/acts`, payload)
      actEditing = null
    })
  }

  /**
   * The Orb's quick action (REDESIGN.md §7). One line, optionally a voice clip.
   * A time can be given; left empty it goes out on the next dispatch pass.
   */
  function orbQuickAction() {
    return `
      <form class="card admin-form" id="orb-form">
        <h2>Send orb message</h2>
        <p class="lede">
          One line the Orb speaks on Home, and the push text when the app is
          closed. No detail page, no body — ${ORB_MAX} characters at most.
        </p>

        <label class="field">
          <span class="field__label">Message</span>
          <input class="field__input" id="orb-message" name="message" maxlength="${ORB_MAX}"
                 required placeholder="Next transmission in three days." />
          <span class="hint"><b id="orb-left">${ORB_MAX}</b> characters left</span>
        </label>

        <label class="field">
          <span class="field__label">When (empty = next dispatch pass)</span>
          <input class="field__input" type="datetime-local" name="publish_at_local" />
        </label>

        <label class="field">
          <span class="field__label">Voice clip (optional)</span>
          <input class="field__input" type="file" name="audio" accept="audio/*" />
          <span class="hint">MP3 or M4A, 8 MB at most. The mesh reacts to it while it plays.</span>
        </label>

        <div class="admin-form__actions">
          <button class="button button--primary" type="submit">Send</button>
          <button class="button button--ghost" type="button" id="orb-cancel">Cancel</button>
        </div>
      </form>
    `
  }

  async function sendOrb(event) {
    event.preventDefault()

    const form = new FormData(event.target)

    // The server reads this as local wall-clock time in the retreat's zone,
    // exactly as it reads the brief form's field of the same name.
    if (String(form.get('publish_at_local') ?? '') === '') {
      form.delete('publish_at_local')
    }

    if (!(form.get('audio') instanceof File) || form.get('audio').size === 0) {
      form.delete('audio')
    }

    await act(async () => {
      const result = await apiPost(`/admin/retreats/${retreatId}/orb`, form)
      orbForm = false
      notice = `Orb message queued${result.has_audio ? ' with a clip' : ''}: “${result.message}”`
    })
  }

  /**
   * The briefs, grouped into the chapters they belong to, in the order they
   * publish.
   *
   * Thirty-five rows in one table is a list you scroll rather than a story you
   * read. The chapter is the only structure the retreat actually has, so it is
   * the one the screen shows — and a point with no act lands in a group of its
   * own rather than disappearing into the middle of another one.
   */
  function chapters(briefs) {
    const order = (data.acts ?? []).map((row) => row.key)
    const groups = new Map()

    for (const brief of briefs) {
      const key = brief.act || ''

      if (!groups.has(key)) groups.set(key, [])

      groups.get(key).push(brief)
    }

    const rank = (key) => {
      const index = order.indexOf(key)

      return index === -1 ? order.length + 1 : index
    }

    return [...groups.entries()]
      .map(([key, list]) => ({
        key,
        title: (data.acts ?? []).find((row) => row.key === key)?.title ?? null,
        briefs: list,
      }))
      .sort((a, b) => rank(a.key) - rank(b.key))
  }

  /**
   * The column headings are repeated only above the first chapter. Five copies
   * of the same five words down one screen is noise, not orientation.
   */
  function chapterBlock(group, now, first) {
    return `
      <section class="achapter">
        <p class="achapter__head">
          <span class="achapter__act">${text(chapterName(group))}</span>
          ${group.title === null ? '' : `<span class="achapter__title">${text(group.title)}</span>`}
          <span class="achapter__count">${group.briefs.length}</span>
        </p>

        <table class="admin-table">
          ${
            first
              ? `<thead>
                   <tr><th>Publishes</th><th>Kind</th><th>Title</th><th>Audience</th><th>Status</th><th></th></tr>
                 </thead>`
              : ''
          }
          <tbody>
            ${group.briefs.map((brief) => briefRow(brief, now)).join('')}
          </tbody>
        </table>
      </section>
    `
  }

  /**
   * What to call a group with no act on it. Orb messages have no chapter by
   * design — they are a line the Orb says, not a point on the Path — so saying
   * "no chapter" about them reads as something being wrong when nothing is.
   */
  function chapterName(group) {
    if (group.key !== '') return group.key

    return group.briefs.every((brief) => brief.kind === 'orb') ? 'Orb messages' : 'No chapter'
  }

  function briefRow(brief, now) {
    const published = brief.publish_at !== null && parseUtc(brief.publish_at) <= now
    const local = brief.publish_at_local?.replace('T', ' ') ?? '—'

    return `
      <tr class="${published ? '' : 'admin-table__row--future'}">
        <td class="admin-table__muted">
          ${local}
          <span class="admin-table__zone">${text(brief.effective_timezone)}</span>
        </td>
        <td class="admin-table__muted">
          <span class="akind akind--${text(brief.kind)}">
            ${text(KINDS.find(([key]) => key === brief.kind)?.[1] ?? brief.kind)}
          </span>
        </td>
        <td>${text(brief.title)}</td>
        <td class="admin-table__muted">${text(brief.audience_name ?? 'everyone')}</td>
        <td><span class="pill pill--${brief.status}">${statusLabel(brief.status)}</span></td>
        <td class="admin-table__actions">
          <button class="link-button" data-edit="${brief.id}">Edit</button>
          <button class="link-button" data-send="${brief.id}">Send now</button>
          <button class="link-button" data-duplicate="${brief.id}">Duplicate</button>
          <button class="link-button link-button--danger" data-delete="${brief.id}">Delete</button>
        </td>
      </tr>
    `
  }

  /**
   * The whole of one point on the Path, in the order a participant meets it:
   * the film, the chapter it belongs to, the title, the quote, the body, the
   * mission. The screens have rendered all of this since the redesign; until
   * now half of it could only be set by editing the seeder in PHP.
   */
  function briefForm(brief) {
    const isNew = brief === 'new'
    const value = isNew ? {} : brief

    return `
      <form class="card admin-form" id="brief-form">
        <h2>${isNew ? 'New brief' : `Edit: ${text(value.title)}`}</h2>

        <p class="admin-form__section">When it arrives</p>

        <div class="admin-form__grid">
          <label class="field">
            <span class="field__label">Kind</span>
            <select class="field__input" name="kind">
              ${KINDS.map(
                ([key, label]) =>
                  `<option value="${key}" ${value.kind === key ? 'selected' : ''}>${label}</option>`,
              ).join('')}
            </select>
          </label>

          <label class="field">
            <span class="field__label">Status</span>
            <select class="field__input" name="status">
              ${STATUSES.map(
                ([key, label]) =>
                  `<option value="${key}" ${value.status === key ? 'selected' : ''}>${label}</option>`,
              ).join('')}
            </select>
          </label>

          <label class="field">
            <span class="field__label">Published (local time)</span>
            <input class="field__input" type="datetime-local" name="publish_at_local"
                   value="${value.publish_at_local ?? ''}" />
          </label>

          <label class="field">
            <span class="field__label">Timezone</span>
            <select class="field__input" name="timezone">
              <option value="">${text(data.retreat.timezone)} (retreat)</option>
              ${data.timezones
                .map(
                  (zone) =>
                    `<option value="${text(zone)}" ${value.timezone === zone ? 'selected' : ''}>${text(zone)}</option>`,
                )
                .join('')}
            </select>
          </label>
        </div>

        <p class="admin-form__section">The chapter it belongs to</p>

        <div class="admin-form__grid">
          <label class="field">
            <span class="field__label">Act</span>
            <input class="field__input" name="act" maxlength="40"
                   value="${text(value.act)}" placeholder="Act II" />
            <span class="hint">Shown above the title, and on Path.</span>
          </label>

          <label class="field">
            <span class="field__label">Act name</span>
            <input class="field__input" name="act_label" maxlength="60"
                   value="${text(value.act_label)}" placeholder="Rhythm" />
            <span class="hint">The chapter's own name. Leave empty to repeat the act.</span>
          </label>
        </div>

        <p class="admin-form__section">What it says</p>

        <label class="field">
          <span class="field__label">Title</span>
          <input class="field__input" name="title" required value="${text(value.title)}" />
        </label>

        <label class="field">
          <span class="field__label">Quote</span>
          <input class="field__input" name="quote" maxlength="300" value="${text(value.quote)}"
                 placeholder="Calm is not the absence of noise." />
          <span class="hint">Sits between the title and the body. The quotation marks are drawn for you.</span>
        </label>

        <label class="field">
          <span class="field__label">Quote author</span>
          <input class="field__input" name="quote_author" maxlength="80" value="${text(value.quote_author)}" />
        </label>

        <label class="field">
          <span class="field__label">Body (Markdown)</span>
          <textarea class="field__input" name="body_md" rows="10">${text(value.body_md)}</textarea>
        </label>

        <p class="admin-form__section">The mission</p>

        <label class="field">
          <span class="field__label">Mission (Markdown)</span>
          <textarea class="field__input" name="mission_md" rows="4">${text(value.mission_md)}</textarea>
          <span class="hint">A tinted card under the body, with a box to tick. Leave empty for no mission.</span>
        </label>

        <label class="field">
          <span class="field__label">Hint</span>
          <input class="field__input" name="mission_hint" maxlength="300" value="${text(value.mission_hint)}" />
          <span class="hint">One line under the mission, for when it feels uncomfortable.</span>
        </label>

        <p class="admin-form__section">Picture and film</p>

        ${
          isNew
            ? '<p class="hint">A picture can be added once the brief is saved.</p>'
            : `<label class="field">
                 <span class="field__label">Picture</span>
                 ${
                   value.image_url
                     ? `<img class="admin-brief__image" src="${text(value.image_url)}" alt="" />`
                     : ''
                 }
                 <input class="field__input" type="file" id="brief-image" accept="image/*" />
                 <span class="hint">
                   Shown on Path and at the top of the brief.
                   ${value.image_url ? '<button type="button" class="link-button" id="brief-image-clear">Remove it</button>' : ''}
                 </span>
               </label>`
        }

        <div class="admin-form__grid">
          <label class="field">
            <span class="field__label">Video (YouTube, Vimeo or Bunny)</span>
            <input class="field__input" name="video_url" value="${text(value.video_url)}"
                   placeholder="https://vimeo.com/…" />
            <span class="hint">A film replaces the picture at the top of the brief.</span>
          </label>

          <label class="field">
            <span class="field__label">Length (minutes)</span>
            <input class="field__input" type="number" name="video_minutes" min="0" max="240"
                   value="${value.video_seconds ? Math.round(value.video_seconds / 60) : ''}" />
            <span class="hint">Shown on the play badge. Leave empty to show none.</span>
          </label>
        </div>

        <p class="admin-form__section">How it is announced</p>

        <div class="admin-form__grid">
          <label class="field">
            <span class="field__label">Push title (3–5 words)</span>
            <input class="field__input" name="push_title" maxlength="80"
                   value="${text(value.push_title)}" />
          </label>

          <label class="field">
            <span class="field__label">Push body (1–2 sentences)</span>
            <input class="field__input" name="push_body" maxlength="200"
                   value="${text(value.push_body)}" />
          </label>

          <label class="field">
            <span class="field__label">Who sees it</span>
            <select class="field__input" name="audience_user_id">
              <option value="">everyone</option>
              ${data.members
                .map(
                  (member) =>
                    `<option value="${member.id}" ${
                      value.audience_user_id === member.id ? 'selected' : ''
                    }>${text(member.name)}</option>`,
                )
                .join('')}
            </select>
          </label>

          <label class="field">
            <span class="field__label">Order</span>
            <input class="field__input" type="number" name="sort_order" value="${value.sort_order ?? 0}" />
          </label>

          <label class="field">
            <span class="field__label">Unlocks</span>
            <select class="field__input" name="unlocks">
              <option value="">nothing</option>
              <option value="group" ${value.unlocks === 'group' ? 'selected' : ''}>group profiles</option>
            </select>
          </label>
        </div>

        <div class="admin-form__actions">
          <button class="button button--primary" type="submit">Save</button>
          <button class="button button--ghost" type="button" id="brief-cancel">Cancel</button>
        </div>
      </form>
    `
  }

  async function submitBrief(event) {
    event.preventDefault()

    const payload = Object.fromEntries(new FormData(event.target))
    payload.retreat_id = retreatId

    // The badge on the play button says minutes, because that is how long a
    // film feels; the column holds seconds, because that is what it is.
    payload.video_seconds = payload.video_minutes ? Number(payload.video_minutes) * 60 : 0
    delete payload.video_minutes

    const isNew = editing === 'new'

    await act(() =>
      isNew ? apiPost('/admin/briefs', payload) : apiPut(`/admin/briefs/${editing.id}`, payload),
    )
  }

  /** Runs a mutation, reloads, and keeps the error on screen when it fails. */
  async function act(work) {
    try {
      notice = null
      await work()
      await reload()
      editing = null
      error = null
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'Spremanje nije uspjelo.'
    }

    draw()
  }

  draw()
}

// ---------------------------------------------------------------------------

/**
 * Splits an upload into requests small enough for a shared host.
 *
 * PHP throws away the whole request — $_POST and $_FILES both — when it
 * exceeds post_max_size, which on a default install is 8 MB. Ten photos from
 * Safari clear that easily, and the failure looks like "no files were sent".
 * Batching keeps every request well under any sane limit.
 */
const BATCH_BYTES = 4 * 1024 * 1024
const BATCH_FILES = 4

function* batches(files) {
  let batch = []
  let bytes = 0

  for (const file of files) {
    if (batch.length > 0 && (bytes + file.size > BATCH_BYTES || batch.length >= BATCH_FILES)) {
      yield batch
      batch = []
      bytes = 0
    }

    batch.push(file)
    bytes += file.size
  }

  if (batch.length > 0) {
    yield batch
  }
}

function errorCount(count) {
  if (count === 0) return 'Errors'

  return `Last ${count} ${count === 1 ? 'error' : 'errors'}`
}

function statusLabel(status) {
  return (
    STATUSES.find(([key]) => key === status)?.[1] ??
    { active: 'Active', template: 'Template', archived: 'Archived', draft: 'Draft' }[status] ??
    status
  )
}



// ---------------------------------------------------------------------------
// Delivery overview
// ---------------------------------------------------------------------------

/**
 * Who gets push, who falls back to email, and whether the cron is alive.
 * This is the screen to look at before a retreat starts.
 */
export async function renderAdminDelivery(root, { onNavigate, onExit }) {
  root.innerHTML = `<div class="admin"><p class="lede">Loading…</p></div>`

  const data = await apiGet('/admin/notifications')

  const onPush = data.people.filter((person) => person.channel === 'push').length
  const unreachable = data.people.filter((person) => !person.reachable).length

  root.innerHTML = `
    <div class="admin">
      ${adminHeader({
        tab: 'delivery',
        subtitle:
          `${onPush} on push · ${data.people.length - onPush} on email` +
          (unreachable > 0 ? ` · ${unreachable} unreachable` : ''),
      })}

      ${data.push_ready ? '' : '<p class="notice notice--error">VAPID keys are not set — push does not work. Run <code>php scripts/vapid.php</code>.</p>'}
      ${data.mail_ready ? '' : '<p class="notice notice--info">SMTP is not set — mail is written to data/logs/mail instead of being sent.</p>'}

      <h2 class="admin__section">Who receives how</h2>
      <table class="admin-table">
        <thead>
          <tr><th>Name</th><th>Channel</th><th>Devices</th><th>Last push</th><th>Delivered</th><th>Failed</th></tr>
        </thead>
        <tbody>
          ${data.people
            .map(
              (person) => `
            <tr>
              <td>${text(person.name)}${person.reachable ? '' : ' <span class="pill pill--draft">unreachable</span>'}</td>
              <td><span class="pill pill--${person.channel === 'push' ? 'sent' : 'scheduled'}">${
                person.channel === 'push' ? 'push' : 'email'
              }</span></td>
              <td class="admin-table__muted">${person.subscriptions}</td>
              <td class="admin-table__muted">${text(person.last_push_at ?? '—')}</td>
              <td class="admin-table__muted">${person.delivered}</td>
              <td class="admin-table__muted">${person.failed}</td>
            </tr>`,
            )
            .join('')}
        </tbody>
      </table>

      <h2 class="admin__section">Cron</h2>
      ${
        data.last_run === null
          ? '<p class="lede">Dispatch has not run yet.</p>'
          : `<p class="lede">
               Last run ${text(data.last_run.started_at)}
               ${data.last_run.finished_at ? '' : ' <strong>(did not finish)</strong>'} ·
               ${data.last_run.briefs} briefs, ${data.last_run.pushes} push,
               ${data.last_run.emails} email, ${data.last_run.failures} failures,
               ${data.last_run.pruned} pruned
               ${data.last_run.note ? `<br /><span class="notice notice--error">${text(data.last_run.note)}</span>` : ''}
             </p>`
      }

      <h2 class="admin__section">Zadnjih 50 isporuka</h2>
      <table class="admin-table">
        <thead>
          <tr><th>When</th><th>To</th><th>What</th><th>Channel</th><th>Outcome</th></tr>
        </thead>
        <tbody>
          ${data.log
            .map(
              (entry) => `
            <tr>
              <td class="admin-table__muted">${text(entry.sent_at)}</td>
              <td>${text(entry.user_name)}</td>
              <td class="admin-table__muted">${text(entry.brief_title ?? '—')}</td>
              <td class="admin-table__muted">${text(entry.channel)}</td>
              <td>${
                Number(entry.ok) === 1
                  ? '<span class="pill pill--sent">ok</span>'
                  : `<span class="pill pill--draft">${text(entry.error ?? 'failed')}</span>`
              }</td>
            </tr>`,
            )
            .join('')}
        </tbody>
      </table>
      ${data.log.length === 0 ? '<p class="lede">Nothing has been sent yet.</p>' : ''}
    </div>
  `

  mountAdminHeader(root, { onNavigate, onExit })
}

// ---------------------------------------------------------------------------
// Settings of one retreat
// ---------------------------------------------------------------------------

/**
 * The retreat itself.
 *
 * Everything on this screen used to come from the seeder and nowhere else: a
 * retreat could be cloned onto new dates and after that nothing about it could
 * be changed from the web — not a typo in the name, not the location, not the
 * rule of the island that opens the Group screen.
 */
export async function renderAdminSettings(root, { retreatId, onNavigate, onExit }) {
  root.innerHTML = `<div class="admin"><p class="lede">Loading…</p></div>`

  let data = await apiGet(`/admin/retreats/${retreatId}`)
  let error = null
  let notice = null
  let busy = false

  /**
   * What the cast's state actually is.
   *
   * It can be open two ways — a switch on this screen, or a published brief
   * that carries the unlock — and only one of them is undoable from here.
   */
  function castLine(group) {
    if (!group.open) {
      return 'Profiles are closed. The Group screen sends no names at all while they are, so there is nothing to reveal early.'
    }

    if (group.by_brief !== null && !group.by_switch) {
      return 'Profiles are open, opened by a brief. Everyone can see everyone.'
    }

    if (group.by_brief !== null) {
      return 'Profiles are open — by the switch below and by a brief. Closing the switch leaves them open.'
    }

    return 'Profiles are open, opened from here. Everyone can see everyone.'
  }

  function draw() {
    const retreat = data.retreat

    root.innerHTML = `
      <div class="admin">
        ${adminHeader({ retreat, tab: 'settings' })}

        ${error ? `<p class="notice notice--error">${text(error)}</p>` : ''}
        ${notice ? `<p class="notice notice--info">${text(notice)}</p>` : ''}

        <form class="card admin-form" id="retreat-form">
          <p class="admin-form__section">What it is</p>

          <label class="field">
            <span class="field__label">Name</span>
            <input class="field__input" name="name" required maxlength="120"
                   value="${text(retreat.name)}" />
          </label>

          <div class="admin-form__grid">
            <label class="field">
              <span class="field__label">Where</span>
              <input class="field__input" name="location" maxlength="120"
                     value="${text(retreat.location)}" placeholder="Fehendhoo, Maldives" />
              <span class="hint">Shown on the Practical screen.</span>
            </label>

            <label class="field">
              <span class="field__label">Call it a…</span>
              <input class="field__input" name="place_noun" maxlength="40"
                     value="${text(retreat.place_noun)}" placeholder="island" list="place-nouns" />
              <datalist id="place-nouns">
                ${['island', 'mountain', 'house', 'city', 'forest', 'boat', 'desert', 'retreat']
                  .map((word) => `<option value="${word}"></option>`)
                  .join('')}
              </datalist>
              <span class="hint">
                One word. The app builds its own phrases from it — “ISLAND MODE”
                under the Orb, “the photos come from the island”.
              </span>
            </label>
          </div>

          <p class="admin-form__section">When</p>

          <div class="admin-form__grid">
            <label class="field">
              <span class="field__label">First day</span>
              <input class="field__input" type="date" name="starts_on" required
                     value="${text(retreat.starts_on)}" />
            </label>

            <label class="field">
              <span class="field__label">Last day</span>
              <input class="field__input" type="date" name="ends_on" required
                     value="${text(retreat.ends_on)}" />
            </label>

            <label class="field">
              <span class="field__label">Timezone</span>
              <select class="field__input" name="timezone">
                ${data.timezones
                  .map(
                    (zone) =>
                      `<option value="${text(zone)}" ${retreat.timezone === zone ? 'selected' : ''}>${text(zone)}</option>`,
                  )
                  .join('')}
              </select>
            </label>

            <label class="field">
              <span class="field__label">Status</span>
              <select class="field__input" name="status">
                ${data.statuses
                  .map(
                    (value) =>
                      `<option value="${text(value)}" ${retreat.status === value ? 'selected' : ''}>${text(statusLabel(value))}</option>`,
                  )
                  .join('')}
              </select>
              <span class="hint">Only one retreat can be live at a time.</span>
            </label>
          </div>

          <label class="checkbox">
            <input type="checkbox" name="move_content" value="1" />
            <span>
              Move the briefs with it. Each keeps its distance from the first day
              and its local time. Leave this off and the dates change while the
              schedule stays where it is.
            </span>
          </label>

          <p class="admin-form__section">Rule of the island</p>

          <label class="field">
            <span class="field__label">Title</span>
            <input class="field__input" name="code_of_conduct_title" maxlength="120"
                   value="${text(retreat.code_of_conduct_title)}" placeholder="Rule of the island" />
          </label>

          <label class="field">
            <span class="field__label">The rule (Markdown)</span>
            <textarea class="field__input" name="code_of_conduct_md" rows="7">${text(retreat.code_of_conduct_md)}</textarea>
            <span class="hint">At the foot of the Group screen, under a vow of confidentiality.</span>
          </label>

          <div class="admin-form__actions">
            <button class="button button--primary" type="submit" ${busy ? 'disabled' : ''}>
              ${busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>

        <section class="card" id="cast">
          <h2>The cast</h2>
          <p class="lede">${castLine(data.group)}</p>
          ${
            data.group.by_brief === null
              ? ''
              : `<p class="hint">
                   “${text(data.group.by_brief.title)}” carries the unlock. The switch
                   below cannot close what a published brief has opened — change the
                   brief if that is what you meant.
                 </p>`
          }
          <div class="admin-form__actions">
            <button class="button ${data.group.by_switch ? 'button--ghost' : 'button--primary'}" id="group-toggle">
              ${data.group.by_switch ? 'Close the switch' : 'Open the profiles'}
            </button>
          </div>
        </section>
      </div>
    `

    mountAdminHeader(root, { onNavigate, onExit })
    root.querySelector('#retreat-form').addEventListener('submit', save)
    root.querySelector('#group-toggle').addEventListener('click', () => toggleGroup(!data.group.by_switch))
  }

  async function save(event) {
    event.preventDefault()

    const payload = Object.fromEntries(new FormData(event.target))

    busy = true
    draw()

    try {
      const result = await apiPut(`/admin/retreats/${retreatId}`, payload)
      data = { ...data, retreat: result.retreat }
      error = null
      notice =
        result.moved > 0
          ? `Saved. ${result.moved} dated ${result.moved === 1 ? 'item' : 'items'} moved with it.`
          : 'Saved.'
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'Could not save the retreat.'
      notice = null
    }

    busy = false
    draw()
  }

  async function toggleGroup(open) {
    try {
      await apiPost(`/admin/retreats/${retreatId}/group-unlock`, { open: open ? '1' : '0' })
      data = await apiGet(`/admin/retreats/${retreatId}`)
      error = null
      notice = open
        ? 'Profiles are open.'
        : data.group.open
          ? 'The switch is off, but a brief still has them open.'
          : 'Profiles are closed again.'
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'That did not work.'
    }

    draw()
  }

  draw()
}

// ---------------------------------------------------------------------------
// Gallery
// ---------------------------------------------------------------------------

/**
 * Upload, day numbers and captions.
 *
 * Photos are downscaled in the browser before they go anywhere: ten straight
 * off a phone is tens of megabytes, and the point of this screen is that it
 * works from the island on a mobile connection.
 */
export async function renderAdminGallery(root, { retreatId, onNavigate, onExit }) {
  root.innerHTML = `<div class="admin"><p class="lede">Loading…</p></div>`

  let data = await apiGet(`/admin/retreats/${retreatId}/gallery`)
  let day = data.today_day ?? 1
  let busy = null
  let error = null
  let notice = null

  async function reload() {
    data = await apiGet(`/admin/retreats/${retreatId}/gallery`)
  }

  function draw() {
    root.innerHTML = `
      <div class="admin">
        ${adminHeader({
          retreat: data.retreat,
          tab: 'gallery',
          subtitle: `${data.total} photos · today is day ${data.today_day ?? '—'}`,
        })}

        ${error ? `<p class="notice notice--error">${text(error)}</p>` : ''}
        ${notice ? `<p class="notice notice--info">${text(notice)}</p>` : ''}

        <section class="card">
          <label class="field">
            <span class="field__label">Day</span>
            <input class="field__input" type="number" id="day" value="${day}" />
          </label>

          <label class="button button--primary upload-button ${busy ? 'is-busy' : ''}">
            ${icon('upload', { size: 18 })}
            <span>${busy ?? 'Choose photos'}</span>
            <input type="file" id="files" accept="image/*" multiple class="visually-hidden"
                   ${busy ? 'disabled' : ''} />
          </label>
          <p class="hint">
            They are downscaled in the browser before sending, so even ten go quickly on mobile.
          </p>
        </section>

        ${data.days.map(dayBlock).join('')}
        ${data.days.length === 0 ? '<p class="lede">No photos yet.</p>' : ''}
      </div>
    `

    mountAdminHeader(root, { onNavigate, onExit })
    // 'input', not 'change': the value must be live without needing a blur,
    // and not every engine fires change on a programmatic edit.
    root.querySelector('#day').addEventListener('input', (event) => {
      day = Number(event.target.value)
    })
    root.querySelector('#files').addEventListener('change', upload)

    root.querySelectorAll('[data-delete-photo]').forEach((button) =>
      button.addEventListener('click', () => {
        if (window.confirm('Delete this photo? It cannot be undone.')) {
          act(() => apiDelete(`/admin/gallery/${button.dataset.deletePhoto}`))
        }
      }),
    )
    root.querySelectorAll('[data-caption]').forEach((input) =>
      input.addEventListener('change', () =>
        act(() =>
          apiPut(`/admin/gallery/${input.dataset.caption}`, {
            caption: input.value,
            day_no: input.dataset.day,
          }),
        ),
      ),
    )
  }

  function dayBlock(group) {
    return `
      <section class="gallery-day">
        <h2 class="gallery-day__title">
          ${group.day_no === null ? 'No day' : `Day ${group.day_no}`} · ${group.items.length}
        </h2>
        <div class="admin-photos">
          ${group.items
            .map(
              (item) => `
            <figure class="admin-photo">
              <img src="${text(item.thumb_url)}" alt="" loading="lazy" />
              <input class="field__input admin-photo__caption" value="${text(item.caption)}"
                     placeholder="Caption" data-caption="${item.id}" data-day="${group.day_no ?? ''}" />
              <button class="link-button link-button--danger" data-delete-photo="${item.id}">Delete</button>
            </figure>`,
            )
            .join('')}
        </div>
      </section>
    `
  }

  async function upload(event) {
    const chosen = [...event.target.files]

    if (chosen.length === 0) return

    error = null
    notice = null

    try {
      busy = `Preparing 0/${chosen.length}…`
      draw()

      const prepared = await downscaleAll(chosen, (done, total) => {
        busy = `Preparing ${done}/${total}…`
        draw()
      })

      let saved = 0
      let sent = 0
      const problems = []

      for (const batch of batches(prepared)) {
        busy = `Sending ${saved + batch.length}/${prepared.length}…`
        draw()

        const form = new FormData()
        form.append('day_no', String(day))
        for (const file of batch) {
          form.append('photos[]', file)
        }

        const result = await apiPost(`/admin/retreats/${retreatId}/gallery`, form)
        saved += result.saved
        sent += result.received_bytes ?? 0
        problems.push(...(result.errors ?? []))
      }

      const megabytes = (sent / 1048576).toFixed(1)
      notice = `Saved ${saved} to day ${day} (${megabytes} MB sent).`

      if (problems.length) {
        error = problems.map((item) => `${item.name}: ${item.message}`).join(' · ')
      }

      await reload()
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'The upload failed.'
    } finally {
      busy = null
      draw()
    }
  }

  async function act(work) {
    try {
      error = null
      await work()
      await reload()
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'That did not work.'
    }

    draw()
  }

  draw()
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

/**
 * Errors, cron health, backups and the destructive operations.
 *
 * The retention run is read-only here on purpose: it is irreversible, so the
 * screen shows what it would do and names the command that does it.
 */
export async function renderAdminSystem(root, { onNavigate, onExit }) {
  root.innerHTML = `<div class="admin"><p class="lede">Loading…</p></div>`

  let data = await apiGet('/admin/system')
  let retention = null
  let notice = null
  let error = null
  let busy = false

  function draw() {
    root.innerHTML = `
      <div class="admin">
        ${adminHeader({
          tab: 'system',
          subtitle:
            `PHP ${data.config.php_version} · schema ${data.config.schema_version} · ` +
            `${data.config.env}${data.config.debug ? ' · debug on' : ''}`,
        })}

        <div class="atoolbar">
          <button class="button button--primary admin__new" id="backup" ${busy ? 'disabled' : ''}>
            ${busy ? 'Working…' : 'Make a backup'}
          </button>
        </div>

        ${error ? `<p class="notice notice--error">${text(error)}</p>` : ''}
        ${notice ? `<p class="notice notice--info">${text(notice)}</p>` : ''}

        ${healthBlock(data)}

        <h2 class="admin__section">${errorCount(data.errors.length)}</h2>
        ${
          data.errors.length === 0
            ? '<p class="lede">Nijedna. Tako i treba.</p>'
            : `<table class="admin-table">
                 <thead><tr><th>When</th><th>Where</th><th>What</th></tr></thead>
                 <tbody>
                   ${data.errors
                     .map(
                       (entry) => `<tr>
                         <td class="admin-table__muted">${text(entry.at)}</td>
                         <td class="admin-table__muted">
                           ${text(entry.channel)}
                           <span class="admin-table__zone">${text(entry.path)}</span>
                         </td>
                         <td>${text(entry.message)}</td>
                       </tr>`,
                     )
                     .join('')}
                 </tbody>
               </table>`
        }

        <h2 class="admin__section">Backups</h2>
        ${
          data.backups.list.length === 0
            ? '<p class="lede">None yet. Cron makes them daily.</p>'
            : `<table class="admin-table">
                 <thead><tr><th>File</th><th>When</th><th>Size</th></tr></thead>
                 <tbody>
                   ${data.backups.list
                     .map(
                       (file) => `<tr>
                         <td>${text(file.file)}</td>
                         <td class="admin-table__muted">${text(file.at)}</td>
                         <td class="admin-table__muted">${(file.bytes / 1048576).toFixed(1)} MB</td>
                       </tr>`,
                     )
                     .join('')}
                 </tbody>
               </table>
               <p class="hint">The last ${data.backups.keep} are kept. Restore instructions are in MANIFEST.txt inside the archive.</p>`
        }

        <h2 class="admin__section">Retention</h2>
        <p class="lede">
          Anonymises retreats that ended more than ${data.config.retention_months} months ago.
        </p>
        <button class="link-button" id="retention">See what would happen</button>
        ${retention === null ? '' : retentionBlock(retention)}
      </div>
    `

    mountAdminHeader(root, { onNavigate, onExit })
    root.querySelector('#backup').addEventListener('click', runBackup)
    root.querySelector('#retention').addEventListener('click', dryRunRetention)
  }

  function healthBlock(system) {
    const cron = system.cron
    const megabytes = (bytes) => `${(bytes / 1048576).toFixed(1)} MB`

    return `
      <div class="health">
        <div class="health__item ${cron.stale ? 'health__item--bad' : 'health__item--ok'}">
          <p class="health__label">Cron</p>
          <p class="health__value">${cron.stale ? 'not reporting' : 'alive'}</p>
          <p class="health__note">
            ${
              cron.last_run === null
                ? 'never run'
                : `last run ${text(cron.last_run.started_at)}${
                    cron.last_run.finished_at ? '' : ' (did not finish)'
                  }`
            }
          </p>
        </div>

        <div class="health__item ${system.backups.stale ? 'health__item--bad' : 'health__item--ok'}">
          <p class="health__label">Backup</p>
          <p class="health__value">${system.backups.count}</p>
          <p class="health__note">
            ${system.backups.latest === null ? 'none' : `last ${text(system.backups.latest.at)}`}
          </p>
        </div>

        <div class="health__item ${system.config.push_ready ? 'health__item--ok' : 'health__item--bad'}">
          <p class="health__label">Push</p>
          <p class="health__value">${system.config.push_ready ? 'ready' : 'not set'}</p>
          <p class="health__note">VAPID keys</p>
        </div>

        <div class="health__item ${system.config.mail_ready ? 'health__item--ok' : ''}">
          <p class="health__label">Email</p>
          <p class="health__value">${system.config.mail_ready ? 'SMTP' : 'to file'}</p>
          <p class="health__note">${system.config.mail_ready ? 'Brevo' : 'data/logs/mail'}</p>
        </div>

        <div class="health__item">
          <p class="health__label">Storage</p>
          <p class="health__value">${megabytes(
            system.storage.database_bytes + system.storage.uploads_bytes,
          )}</p>
          <p class="health__note">+ ${megabytes(system.storage.backups_bytes)} backupa</p>
        </div>
      </div>
    `
  }

  function retentionBlock(report) {
    if (report.retreats.length === 0) {
      return '<p class="lede">No retreats older than the cutoff.</p>'
    }

    return `
      <table class="admin-table">
        <thead><tr><th>Retreat</th><th>Ended</th><th>People</th></tr></thead>
        <tbody>
          ${report.retreats
            .map(
              (retreat) => `<tr>
                <td>${text(retreat.name)}</td>
                <td class="admin-table__muted">${text(retreat.ended)}</td>
                <td class="admin-table__muted">${retreat.people}</td>
              </tr>`,
            )
            .join('')}
        </tbody>
      </table>
      <p class="hint">
        That was a dry run. It only executes from the terminal:
        <code>php scripts/retention.php --apply</code>
      </p>
    `
  }

  async function runBackup() {
    busy = true
    error = null
    notice = null
    draw()

    try {
      const result = await apiPost('/admin/system/backup')
      notice = `${result.file} — ${(result.bytes / 1048576).toFixed(1)} MB, ${result.uploads} datoteka.`
      data = await apiGet('/admin/system')
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'The backup failed.'
    } finally {
      busy = false
      draw()
    }
  }

  async function dryRunRetention() {
    try {
      retention = (await apiGet('/admin/system/retention')).report
    } catch (problem) {
      error = problem instanceof ApiError ? problem.message : 'Provjera nije uspjela.'
    }

    draw()
  }

  draw()
}
